import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchECGFeatures,
  withD1NAMOErrorHandling,
} from "../services/api-config";
import type { ECGFeatures, ECGFeaturesResponse } from "../types/api";

/**
 * Hook for fetching ECG features for a patient
 * Provides comprehensive data management with caching, error handling, and real-time updates
 */
export function useECGFeatures(
  patientId?: string,
  options?: {
    startDate?: string;
    endDate?: string;
    rhythmClassification?: string;
    enabled?: boolean;
  },
) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [
      "ecg-features",
      patientId,
      options?.startDate,
      options?.endDate,
      options?.rhythmClassification,
    ],
    queryFn: async ({ signal: _signal }) => {
      if (!patientId) {
        throw new Error("patientId is required for ECG features");
      }

      return withD1NAMOErrorHandling(
        () =>
          fetchECGFeatures(patientId, {
            startDate: options?.startDate,
            endDate: options?.endDate,
            rhythmClassification: options?.rhythmClassification,
          }),
        {
          endpoint: "/ecg",
          patientId,
          operation: "fetch ECG features",
        },
      );
    },
    enabled: Boolean(patientId) && options?.enabled !== false,
    staleTime: 2 * 60 * 1000, // 2 minutes - ECG data may update frequently
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (error && typeof error === "object" && "status" in error) {
        const status = error.status as number;
        return status >= 500 && failureCount < 3;
      }
      return failureCount < 2;
    },
  });

  /**
   * Mutation for adding new ECG features
   */
  const addECGFeatures = useMutation({
    mutationFn: async (features: Omit<ECGFeatures, "id" | "createdAt">) => {
      if (!patientId) {
        throw new Error("patientId is required");
      }

      const response = await fetch("/api/ecg", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify({
          ...features,
          patientId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to add ECG features: ${response.statusText}`);
      }

      return response.json() as Promise<ECGFeatures>;
    },
    onMutate: async (newFeatures) => {
      await queryClient.cancelQueries({
        queryKey: ["ecg-features", patientId],
      });

      const previousData = queryClient.getQueryData<ECGFeaturesResponse>([
        "ecg-features",
        patientId,
      ]);

      const optimisticFeatures: ECGFeatures = {
        ...newFeatures,
        id: `temp-${Date.now()}`,
        createdAt: new Date().toISOString(),
      };

      queryClient.setQueryData<ECGFeaturesResponse>(
        ["ecg-features", patientId],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            data: [...(old.data || []), optimisticFeatures],
            metadata: {
              ...old.metadata,
              count: (old.metadata?.count || 0) + 1,
            },
          };
        },
      );

      return { previousData };
    },
    onError: (_err, _newFeatures, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ["ecg-features", patientId],
          context.previousData,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["ecg-features", patientId] });
    },
  });

  /**
   * Mutation for updating existing ECG features
   */
  const updateECGFeatures = useMutation({
    mutationFn: async (features: ECGFeatures) => {
      const response = await fetch(`/api/ecg/${features.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify(features),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to update ECG features: ${response.statusText}`,
        );
      }

      return response.json() as Promise<ECGFeatures>;
    },
    onMutate: async (updatedFeatures) => {
      await queryClient.cancelQueries({
        queryKey: ["ecg-features", patientId],
      });

      const previousData = queryClient.getQueryData<ECGFeaturesResponse>([
        "ecg-features",
        patientId,
      ]);

      queryClient.setQueryData<ECGFeaturesResponse>(
        ["ecg-features", patientId],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((features: any) =>
              features.id === updatedFeatures.id ? updatedFeatures : features,
            ),
          };
        },
      );

      return { previousData };
    },
    onError: (_err, _updatedFeatures, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ["ecg-features", patientId],
          context.previousData,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["ecg-features", patientId] });
    },
  });

  /**
   * Mutation for deleting ECG features
   */
  const deleteECGFeatures = useMutation({
    mutationFn: async (featuresId: string) => {
      const response = await fetch(`/api/ecg/${featuresId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to delete ECG features: ${response.statusText}`,
        );
      }

      return featuresId;
    },
    onMutate: async (featuresId) => {
      await queryClient.cancelQueries({
        queryKey: ["ecg-features", patientId],
      });

      const previousData = queryClient.getQueryData<ECGFeaturesResponse>([
        "ecg-features",
        patientId,
      ]);

      queryClient.setQueryData<ECGFeaturesResponse>(
        ["ecg-features", patientId],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.filter(
              (features: any) => features.id !== featuresId,
            ),
            metadata: {
              ...old.metadata,
              count: Math.max((old.metadata?.count || 0) - 1, 0),
            },
          };
        },
      );

      return { previousData };
    },
    onError: (_err, _featuresId, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ["ecg-features", patientId],
          context.previousData,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["ecg-features", patientId] });
    },
  });

  /**
   * Utility function to refetch ECG features
   */
  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ["ecg-features", patientId] });
  };

  /**
   * Utility function to get ECG summary statistics
   */
  const getECGSummary = () => {
    if (!query.data?.data) return null;

    const features = query.data.data;
    const heartRates = features.map((f) => f.heartRate);
    const avgHeartRate =
      heartRates.length > 0
        ? heartRates.reduce((sum, hr) => sum + hr, 0) / heartRates.length
        : 0;

    const rhythmClassifications = features.reduce(
      (acc, f) => {
        acc[f.waveformFeatures.rhythmClassification] =
          (acc[f.waveformFeatures.rhythmClassification] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    const avgHRV =
      features.length > 0
        ? features.reduce(
            (sum, f) => sum + f.hrvFeatures.heartRateVariability,
            0,
          ) / features.length
        : 0;

    return {
      totalRecordings: features.length,
      averageHeartRate: Math.round(avgHeartRate),
      averageHRV: Math.round(avgHRV * 100) / 100,
      rhythmClassifications,
      mostRecentRecording: features.sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      )[0],
      arrhythmiaCount: rhythmClassifications.arrhythmia || 0,
      normalRhythmCount: rhythmClassifications.normal || 0,
    };
  };

  /**
   * Utility function to get HRV trends over time
   */
  const getHRVTrends = () => {
    if (!query.data?.data) return [];

    return query.data.data
      .sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      )
      .map((features) => ({
        timestamp: features.timestamp,
        heartRate: features.heartRate,
        hrv: features.hrvFeatures.heartRateVariability,
        rmssd: features.hrvFeatures.rmssd,
        sdnn: features.hrvFeatures.sdnn,
        rhythmClassification: features.waveformFeatures.rhythmClassification,
      }));
  };

  return {
    // Query state
    data: query.data?.data || [],
    metadata: query.data?.metadata,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isSuccess: query.isSuccess,

    // Mutations
    addECGFeatures: addECGFeatures.mutate,
    updateECGFeatures: updateECGFeatures.mutate,
    deleteECGFeatures: deleteECGFeatures.mutate,

    // Mutation states
    isAdding: addECGFeatures.isPending,
    isUpdating: updateECGFeatures.isPending,
    isDeleting: deleteECGFeatures.isPending,

    // Utilities
    refetch,
    getECGSummary,
    getHRVTrends,
  };
}

/**
 * Hook for fetching ECG features with pagination support
 */
export function useECGFeaturesPaginated(
  patientId?: string,
  options?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    rhythmClassification?: string;
  },
) {
  const { page = 1, limit = 50 } = options || {};

  return useQuery({
    queryKey: [
      "ecg-features-paginated",
      patientId,
      page,
      limit,
      options?.startDate,
      options?.endDate,
      options?.rhythmClassification,
    ],
    queryFn: async ({ signal: _signal }) => {
      if (!patientId) {
        throw new Error("patientId is required for ECG features");
      }

      return withD1NAMOErrorHandling(
        () =>
          fetchECGFeatures(patientId, {
            startDate: options?.startDate,
            endDate: options?.endDate,
            rhythmClassification: options?.rhythmClassification,
          }),
        {
          endpoint: "/ecg",
          patientId,
          operation: "fetch paginated ECG features",
        },
      );
    },
    enabled: Boolean(patientId),
    staleTime: 2 * 60 * 1000,
    select: (data) => {
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedData = data.data?.slice(startIndex, endIndex) || [];

      return {
        data: paginatedData,
        metadata: {
          ...data.metadata,
          currentPage: page,
          totalPages: Math.ceil((data.metadata?.count || 0) / limit),
          hasNextPage: endIndex < (data.metadata?.count || 0),
          hasPreviousPage: page > 1,
        },
      };
    },
  });
}

/**
 * Hook for fetching real-time ECG monitoring data
 * Uses shorter stale time and more frequent refetching
 */
export function useECGRealTime(
  patientId?: string,
  options?: {
    refetchInterval?: number;
    enabled?: boolean;
  },
) {
  return useQuery({
    queryKey: ["ecg-realtime", patientId],
    queryFn: async ({ signal: _signal }) => {
      if (!patientId) {
        throw new Error("patientId is required for real-time ECG monitoring");
      }

      return withD1NAMOErrorHandling(
        () =>
          fetchECGFeatures(patientId, {
            startDate: new Date(Date.now() - 60 * 60 * 1000).toISOString(), // Last hour
          }),
        {
          endpoint: "/ecg",
          patientId,
          operation: "fetch real-time ECG features",
        },
      );
    },
    enabled: Boolean(patientId) && options?.enabled !== false,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: options?.refetchInterval || 60 * 1000, // 1 minute default
    refetchOnWindowFocus: true,
    select: (data) => {
      // Return only the most recent recording for real-time monitoring
      const sortedData = data.data?.sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      );
      return sortedData?.[0] || null;
    },
  });
}
